// 作物绘制引擎 —— 每个品种有自己的形态档案，随生长进度连续变化。
// plantArt(crop, progress0~1, alive, windy, health) 返回 SVG 片段，
// 坐标系：原点=土面中心，向上为 -y；无 CSS 变量（分享卡可独立渲染）。
// 通用发芽序列：土包隆起 → 芽钩破土 → 双子叶展开 → 真叶接管（子叶渐渐枯落）。
const plantArt = (() => {
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const t = (x, a, b) => clamp((x - a) / (b - a), 0, 1);
  // 出口调色与混色统一走公共美术工具层（js/artkit.js）——光照/色板/调色全场只能有一个实现，
  // 否则各画各的是必然结果。⚠️ artkit.js 必须在本文件之前加载；单测直接 require 本文件时
  // 拿不到全局 ART，所以这里两路都留。
  const AK = typeof ART !== 'undefined' ? ART : require('./artkit.js');
  const wcArt = AK.wcArt;
  const lerp = (a, b, k) => a + (b - a) * k;
  const px = n => +(+n).toFixed(2);
  const mixc = AK.mixc;   // 混色输出 hex，保证结果可以继续参与混色
  // 确定性微抖动：同一株植物永远长同一个样，但株株略有不同
  const hash = s => {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
    return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
  };
  const jit = (id, i, span) => (hash(`${id}|${i}`) - 0.5) * span;

  // ═══ 可数器官：每天打开都要有一处肉眼看得见的不同 ═══
  // 株型体量（茎高/株幅/根冠比）保持连续生长——那是积温模型的直接产物，也是本作的命根子；
  // 但**能一个一个数出来**的器官（叶、花、果、根肩）改成离散步进：某一天多抽一片叶、
  // 多坐一颗果、有一颗转红。整株长大 3% 人眼看不出来，"今天多了一颗红果"看得出来。
  //
  // ⚠️ 同一份排产表既驱动画面、也驱动播报（见文件末尾 plantCensus）——画面上数得出几颗，
  // 播报里就说几颗，两边永远不会打架。以后新增作物的可数器官必须同时接上这两处。
  const LEAF_STEPS = 3;      // 一片叶从抽出到完全展开，分 3 档
  const RIPE_STEPS = 3;      // 转色：青 → 转色 → 将熟 → 熟透
  const SIZE_STEPS = 3;      // 果实膨大：幼果 → 中果 → 定个头
  const qstep = (x, n) => Math.round(clamp(x, 0, 1) * n) / n;

  // 叶序：第 i 片叶在 start+i*step 进度点抽出，span 之内展开完毕
  const seqOpen = (p, sq, i) => qstep((p - (sq.start + i * sq.step)) / sq.span, LEAF_STEPS);
  const seqCount = (p, sq, n) => { let k = 0; for (let i = 0; i < n; i++) if (p >= sq.start + i * sq.step) k++; return k; };
  // 展开档位总和：一片叶从抽出到展平要走 3 档，把所有叶的档位加起来，
  // 变化频率是"叶片数"的三倍——叶菜的中后期就是靠它才不至于一连十天毫无动静
  const seqUnfurl = (p, sq, n) => { let k = 0; for (let i = 0; i < n; i++) k += seqOpen(p, sq, i) * LEAF_STEPS; return Math.round(k); };
  // ⚠️ start/step 要让最后一片叶落在 0.8 以后：叶序排完之后如果还剩很长一段生长期，
  // 那段时间画面就彻底静止了（这正是改造前叶菜后半程"看不出任何变化"的原因）
  const SEQ = {
    rosette: { start: 0.08, step: 0.075, span: 0.10 },
    root:    { start: 0.08, step: 0.05,  span: 0.10 },
    spears:  { start: 0.08, step: 0.05,  span: 0.14 },
    bush:    { start: 0.09, step: 0.06,  span: 0.12 },
    staked:  { start: 0.10, step: 0.12,  span: 0.10 },
    pepper:  { start: 0.12, step: 0.09,  span: 0.12 },
    vine:    { start: 0.14, step: 0.11,  span: 0.12 },
    sprawl:  { start: 0.09, step: 0.075, span: 0.11 },
    corn:    { start: 0.10, step: 0.09,  span: 0.10 },
    berry:   { start: 0.09, step: 0.07,  span: 0.11 },
    sunfl:   { start: 0.10, step: 0.09,  span: 0.10 },
    marigold:{ start: 0.09, step: 0.06,  span: 0.10 }
  };
  const HERB_SEQ = {
    round: { start: 0.08, step: 0.085, span: 0.12 },
    stem:  { start: 0.06, step: 0.05,  span: 0.10 },
    bushy: { start: 0.08, step: 0.10,  span: 0.14 }
  };

  // 果实排产表：n 颗果在 [from,to] 这段进度里**逐颗**坐果，每颗坐果后经 ripe 段转色。
  // 逐颗而非整穗，是为了把"多一颗"这件事拆到日粒度上——一茬番茄 8 颗果 × (坐果+3 档转色)
  // ＝ 32 次离散变化，摊在结果期的十几天里，几乎每天都能数出不同。
  function fruitPlan(cfg) {
    const n = cfg.n, from = cfg.from, to = cfg.to, span = cfg.ripe;
    return Array.from({ length: n }, (_, i) => {
      const setAt = from + (to - from) * (i / Math.max(1, n - 1 || 1));
      return { i, setAt, ripeFrom: setAt + span * 0.2, ripeTo: setAt + span };
    });
  }
  // 当前进度下每颗果的离散状态；未坐果的不在列表里（长度即"看得见几颗"）
  function fruitState(p, plan) {
    const out = [];
    for (const f of plan) {
      if (p < f.setAt) continue;
      const grown = qstep((p - f.setAt) / Math.max(0.02, (f.ripeTo - f.setAt) * 0.7), SIZE_STEPS);
      out.push({
        i: f.i,
        size: Math.max(1 / SIZE_STEPS, grown),
        ripe: qstep((p - f.ripeFrom) / Math.max(0.02, f.ripeTo - f.ripeFrom), RIPE_STEPS)
      });
    }
    return out;
  }
  const ripeCount = fs => fs.filter(f => f.ripe >= 1).length;
  // 排产表只跟品种配置有关，按 cfg 对象缓存（每帧重算 26 个品种的表没必要）
  const PLAN_CACHE = new WeakMap();
  const planOf = cfg => {
    let pl = PLAN_CACHE.get(cfg);
    if (!pl) { pl = fruitPlan(cfg.fruits || { n: 5, from: 0.6, to: 0.88, ripe: 0.14 }); PLAN_CACHE.set(cfg, pl); }
    return pl;
  };

  // 花序：n 朵**逐朵**开（开合分 2 档），到 fade 进度点起再按同样顺序逐朵谢。
  // 返回此刻还看得见的那几朵——"今天多开了一朵"同样是数得出来的事。
  function bloomSet(p, n, from, to, fade) {
    const out = [];
    const gap = Math.max(0.02, (to - from) / Math.max(1, n - 1 || 1));
    for (let i = 0; i < n; i++) {
      const at = from + gap * i;
      if (p < at) continue;
      if (fade != null && p >= fade + gap * i) continue;
      out.push({ i, open: Math.max(0.5, qstep((p - at) / gap, 2)) });
    }
    return out;
  }

  const put = (art, x, y, rot, s) => s <= 0.02 ? '' :
    `<g transform="translate(${px(x)},${px(y)}) rotate(${px(rot)}) scale(${px(s)})">${art}</g>`;

  // ═══ 体积：让果实在几像素大小时也读得出"一颗球" ═══
  // 手机上一颗番茄只有十几个物理像素，纯色圆＋一个白点在这个尺寸下会糊成一坨色块，
  // "今天多红了一颗"就看不出来。用三层不透明色堆出球感（暗底 → 亮面偏左上 → 高光），
  // **刻意不用 radialGradient**：渐变要 defs+id，而这段画稿会同时出现在场景、分享卡、
  // 图鉴等多个 SVG 文档里，id 重复与"独立文档里引用悬空"都踩过（见 wc-soft 的教训）。
  // 三层实色到处都能画，也更省。
  function orb(cx, cy, r, base, opts = {}) {
    const lit = opts.lit || mixc(base, '#ffffff', 0.28);
    const dark = opts.dark || mixc(base, '#2a1c10', 0.22);
    const k = opts.flat || 1;                       // <1 表示压扁（南瓜等）
    return `<ellipse cx="${px(cx)}" cy="${px(cy)}" rx="${px(r)}" ry="${px(r * k)}" fill="${dark}"/>
      <ellipse cx="${px(cx - r * 0.1)}" cy="${px(cy - r * 0.12 * k)}" rx="${px(r * 0.88)}" ry="${px(r * 0.86 * k)}" fill="${base}"/>
      <ellipse cx="${px(cx - r * 0.26)}" cy="${px(cy - r * 0.3 * k)}" rx="${px(r * 0.5)}" ry="${px(r * 0.44 * k)}" fill="${lit}"/>
      <ellipse cx="${px(cx - r * 0.34)}" cy="${px(cy - r * 0.4 * k)}" rx="${px(r * 0.22)}" ry="${px(r * 0.16 * k)}" fill="#fff" opacity="0.55"/>`;
  }
  // 接触阴影：果实落在叶子/地面上的一小片暗，负责把果实从背景里"拔"出来
  const contact = (cx, cy, r) =>
    `<ellipse cx="${px(cx)}" cy="${px(cy)}" rx="${px(r * 1.05)}" ry="${px(r * 0.3)}" fill="#22160c" opacity="0.16"/>`;
  // 逐颗微差：同一株上的果子不该是复制粘贴的——大小与色相各自偏一点，
  // 眼睛才分得清"这颗昨天就有""那颗今天才红"
  const vary = (id, i, span) => 1 + (hash(`${id}|orb|${i}`) - 0.5) * span;

  // ── 叶片图形（一律朝右画、长约 20，由 put() 旋转缩放摆位）──
  // 每片叶＝底衬（比正面深一档、下移一点＝叶片的厚度与自投影）+ 正面 + 主脉 + 侧脉。
  // 侧脉是这次补的：一片叶在屏幕上有二三十像素时，只有一根主脉会读成一块色斑；
  // 有了分叉的脉络，眼睛才认得出"这是一片叶子"，也才看得出叶子在一片片变多。
  // ⚠️ 2026-09-27 精修（用户："植物的精细化做得不好，像是草图"）。放大到主角尺寸之后三个毛病一眼可见：
  //   ① 叶脉是近乎白色的粗线，像在叶面上拿粉笔划了几道——草图感的头号来源；
  //   ② 每片叶一整块平涂，没有受光面与背光面，叶子读不出"是一片会弯的薄片"；
  //   ③ 相邻叶片同色相接，边界全靠那层错位的深色衬底，叠在一起就糊成一团。
  // 对应改法（全在这一层，所有作物一起受益，风格仍是水彩绘本，只做加法）：
  //   · 叶脉改用**同色相的浅一档**（叶色往浅色里混 45%），细、半透明——看得见纹理，不再是划痕；
  //   · 每片叶按主脉切成上下两半：上半受光（左上 45° 光，与 DESIGN.md 视觉规范一致）提亮，下半压暗；
  //   · 正面描一道比叶色深的细边，叠在一起的叶片各自分得开。
  // 半叶路径是逐个手写的（与叶形同一组控制点），不用 clipPath：这段画稿会出现在场景、分享卡、
  // 图鉴等多个独立 SVG 文档里，defs+id 的引用悬空与重复都踩过（见 orb 的注释）。
  // 浅色叶脉（绝大多数作物传进来的是近白的黄绿）往叶色里混——那才是"粉笔划痕"的来源；
  // 但**有颜色的叶脉是作物的身份**（甜菜根、瑞士甜菜的红脉），原样保留，不能混成泥色
  const pale = c => { const n = parseInt(c.slice(1), 16); return ((n >> 16) & 255) > 170 && ((n >> 8) & 255) > 170 && (n & 255) > 140; };
  const veinCol = (d, l) => pale(l) ? mixc(d, l, 0.45) : l;
  const veins = (l, pairs) => `<g stroke="${l}" stroke-width="0.5" fill="none" opacity="0.55" stroke-linecap="round">
    ${pairs.map(([x1, y1, x2, y2]) => `<path d="M${x1},${y1} L${x2},${y2}"/>`).join('')}</g>`;
  const rim = d => `stroke="${mixc(d, '#10240c', 0.45)}" stroke-width="0.45" stroke-opacity="0.7" stroke-linejoin="round"`;
  // 一片叶的通用组装：自投影衬底 → 正面（带描边）→ 上半受光 → 下半背光 → 主脉 → 侧脉
  const leafBody = (shape, up, low, d, mid, vein, shadowTint = '#1e3a14') =>
    `<path d="${shape}" fill="${mixc(d, shadowTint, 0.35)}" transform="translate(0.6,1.3)" opacity="0.9"/>
      <path d="${shape}" fill="${d}" ${rim(d)}/>
      <path d="${up}" fill="#fffbe8" opacity="0.13"/>
      <path d="${low}" fill="#0e2008" opacity="0.14"/>
      <path d="${mid}" stroke="${vein}" stroke-width="0.9" fill="none" opacity="0.8" stroke-linecap="round"/>`;
  const LEAF = {
    oval: (d, l) => { const v = veinCol(d, l); return leafBody(
      'M0,0 C 5,-8 15,-9 21,-2 C 15,4 5,4 0,0 Z',
      'M0,0 C 5,-8 15,-9 21,-2 C 15,-3.2 7,-2.6 0,0 Z',
      'M0,0 C 7,-2.6 15,-3.2 21,-2 C 15,4 5,4 0,0 Z', d,
      'M1.5,-0.5 C 8,-2.8 15,-3.2 20,-2', v)
      + veins(v, [[6, -2.2, 8.5, -5.8], [6, -2.2, 7.8, 1.4], [11, -2.8, 13.5, -6.4], [11, -2.8, 12.8, 1.2], [15.5, -3, 17.4, -5.6], [15.5, -3, 16.8, 0.4]]); },
    round: (d, l) => { const v = veinCol(d, l); return leafBody(
      'M0,0 C 3,-9 14,-12 20,-6 C 24,-1 20,5 13,5.5 C 6,6 1,4 0,0 Z',
      'M0,0 C 3,-9 14,-12 20,-6 C 21.6,-4.2 21.4,-3.4 20.6,-3 C 14,-4.2 8,-3.8 0,0 Z',
      'M0,0 C 8,-3.8 14,-4.2 20.6,-3 C 21.6,1 18,5.3 13,5.5 C 6,6 1,4 0,0 Z', d,
      'M1.5,-0.8 C 8,-3.8 14,-4.2 19.5,-3', v)
      + veins(v, [[6, -2.6, 8.4, -7.2], [6, -2.6, 7.2, 2.4], [11, -3.6, 13.8, -8.4], [11, -3.6, 12.2, 3.4], [15.8, -3.8, 18.2, -6.6], [15.8, -3.8, 17, 1.8]]); },
    curly: (d, l) => { const v = veinCol(d, l); return leafBody(
      'M0,0 C 3,-6 5,-9 9,-7 C 11,-10 15,-10 17,-7 C 20,-8 23,-5 21,-2 C 23,1 20,4 16,3 C 14,6 9,6 7,3 C 4,5 1,3 0,0 Z',
      'M0,0 C 3,-6 5,-9 9,-7 C 11,-10 15,-10 17,-7 C 20,-8 23,-5 21,-2 C 15,-2.8 8,-2.2 0,0 Z',
      'M0,0 C 8,-2.2 15,-2.8 21,-2 C 23,1 20,4 16,3 C 14,6 9,6 7,3 C 4,5 1,3 0,0 Z', d,
      'M1.5,-0.5 Q 10,-2.8 19.5,-2', v, '#16321a')
      // 卷叶的皱褶：沿叶缘几道短弧，比直线侧脉更像羽衣甘蓝
      + `<path d="M8.6,-6.4 q 0.8,1.6 0.2,3 M14.2,-7.8 q 0.6,1.8 -0.2,3.2 M19.2,-5.6 q 0,1.6 -1,2.6 M9,4.6 q 0.4,-1.6 -0.4,-3 M15.2,3.6 q 0.2,-1.4 -0.6,-2.6"
          stroke="${mixc(d, '#0e2008', 0.3)}" stroke-width="0.55" fill="none" opacity="0.6" stroke-linecap="round"/>`
      + veins(v, [[6, -1.6, 8, -5], [11.5, -2.4, 13.2, -6], [16.2, -2.2, 17.6, -4.8], [11.5, -2.4, 12.8, 1.8]]); },
    spoon: (d, l, pet) => { const v = veinCol(d, l); return `<path d="M0,0 Q 5,-1.2 9,-1" stroke="${pet}" stroke-width="2.4" fill="none" stroke-linecap="round"/>
      <path d="M0,0 Q 5,-1.2 9,-1" stroke="#ffffff" stroke-width="0.7" fill="none" stroke-linecap="round" opacity="0.25" transform="translate(0,-0.5)"/>`
      + leafBody('M7,-2 A 8,6 0 1 1 23,-2 A 8,6 0 1 1 7,-2 Z',
        'M7,-2 A 8,6 0 1 1 23,-2 Q 15,-3.4 7,-2 Z',
        'M7,-2 Q 15,-3.4 23,-2 A 8,6 0 1 1 7,-2 Z', d,
        'M8.5,-1.8 Q 14.5,-3.4 21.5,-2.2', v)
      + veins(v, [[12, -2.6, 13.6, -6.6], [12, -2.6, 12.8, 2], [16.4, -2.9, 18, -6.4], [16.4, -2.9, 17.2, 1.8]]); },
    fern: d => `<g stroke="${d}" stroke-width="1.5" fill="none" stroke-linecap="round">
      <path d="M0,0 Q 12,-3.5 24,-10"/>
      <path d="M5,-1.5 L 8,-7 M5,-1.5 L 9,-0.2 M10,-3 L 14,-9 M10,-3 L 15,-2 M15,-5.5 L 19,-11.5 M15,-5.5 L 20,-4.6 M20,-8 L 23,-13 M20,-8 L 24,-7"/></g>
      <g stroke="${mixc(d, '#ffffff', 0.25)}" stroke-width="0.6" fill="none" stroke-linecap="round" opacity="0.6" transform="translate(-0.3,-0.4)">
      <path d="M5,-1.5 L 8,-7 M10,-3 L 14,-9 M15,-5.5 L 19,-11.5 M20,-8 L 23,-13"/></g>`,
    lobed: (d, l) => { const v = veinCol(d, l); return leafBody(
      'M0,0 C 2,-5 4,-9 9,-10 C 9,-14 15,-16 18,-12 C 23,-13 26,-8 23,-4 C 26,0 22,5 17,4 C 14,8 6,7 4,3 C 1,3 -1,2 0,0 Z',
      'M0,0 C 2,-5 4,-9 9,-10 C 9,-14 15,-16 18,-12 C 23,-13 26,-8 23,-4 C 21,-5.5 18,-7.4 16,-7 C 10,-5 5,-2.6 0,0 Z',
      'M0,0 C 5,-2.6 10,-5 16,-7 C 18,-7.4 21,-5.5 23,-4 C 26,0 22,5 17,4 C 14,8 6,7 4,3 C 1,3 -1,2 0,0 Z', d,
      'M2,-0.8 L 18.5,-7.6 M8,-3 L 10,-10.4 M13,-5.2 L 20,-3.2', v, '#1b3512')
      + veins(v, [[7, -3.4, 9.5, -8.6], [11, -5.2, 13, -10.4], [10, -4.6, 11, 1.6], [15, -6.2, 17.5, -9.6], [14, -5.6, 15, 0.8], [18, -7, 21, -5.4]]); },
    heart: (d, l) => { const v = veinCol(d, l); return leafBody(
      'M0,0 C 3,-7 8,-11 14,-11 C 20,-11 24,-6 22,-1 C 20,4 12,6 6,4 C 2,3 -1,2 0,0 Z',
      'M0,0 C 3,-7 8,-11 14,-11 C 20,-11 24,-6 22,-1 C 16,-3.6 9,-3.4 0,0 Z',
      'M0,0 C 9,-3.4 16,-3.6 22,-1 C 20,4 12,6 6,4 C 2,3 -1,2 0,0 Z', d,
      'M1.5,-0.5 C 9,-3.6 16,-4.4 21,-2.4', v)
      + veins(v, [[7, -2.6, 9.2, -7.4], [7, -2.6, 8, 2.4], [12.5, -3.5, 15.2, -8.2], [12.5, -3.5, 13.5, 3.4], [17.5, -3.6, 19.8, -7], [17.5, -3.6, 18.5, 1.4]]); }
  };

  // 复叶（番茄/土豆）：一根小叶柄带 5 片小裂叶
  // 复叶（番茄/土豆）：一根小叶柄带 5 片小裂叶。小叶原来是纯椭圆——一株番茄远看是一串鱼骨，
  // 现在改成尖头、带锯齿感的小叶，同样切上下半做受光/背光
  const compound = (d, l) => { const v = veinCol(d, l); const leaflet = `<path d="M0,0 C 2,-3.6 7,-4 10,-0.4 C 7,2.6 2,2.4 0,0 Z" fill="${mixc(d, '#1e3a14', 0.35)}" transform="translate(0.4,0.8)"/>
        <path d="M0,0 C 2,-3.6 7,-4 10,-0.4 C 7,2.6 2,2.4 0,0 Z" fill="${d}" ${rim(d)}/>
        <path d="M0,0 C 2,-3.6 7,-4 10,-0.4 C 6.6,-0.9 3,-0.6 0,0 Z" fill="#fffbe8" opacity="0.14"/>
        <path d="M0,0 C 3,-0.6 6.6,-0.9 10,-0.4 C 7,2.6 2,2.4 0,0 Z" fill="#0e2008" opacity="0.13"/>
        <path d="M1,-0.2 L 9,-0.5" stroke="${v}" stroke-width="0.6" opacity="0.8"/>
        <path d="M3.4,-0.3 L 4.8,-2.2 M3.4,-0.3 L 4.6,1.4 M6.2,-0.4 L 7.4,-2 M6.2,-0.4 L 7.2,1.1" stroke="${v}" stroke-width="0.4" opacity="0.5" fill="none"/>`;
    return `<path d="M0,0 Q 10,-2 19,-3" stroke="${mixc(d, '#2a3a14', 0.2)}" stroke-width="1.4" fill="none" stroke-linecap="round"/>
    ${[[3.6, -0.7, -52, 0.86], [7.6, -1.5, 34, 0.9], [11.4, -2.2, -48, 0.96], [15.2, -2.7, 30, 1], [19, -3, -8, 1.1]]
      .map(([x, y, r, sc]) => put(leaflet, x, y, r, sc)).join('')}`; };

  // 五瓣小花
  const bloom5 = (petal, heart, r = 3) => `${[0, 72, 144, 216, 288].map(a =>
    `<ellipse cx="0" cy="${px(-r * 0.75)}" rx="${px(r * 0.42)}" ry="${px(r * 0.8)}" fill="${petal}" transform="rotate(${a})"/>`).join('')}
    <circle r="${px(r * 0.42)}" fill="${heart}"/>`;
  // 葫芦科大黄花（喇叭形）
  const trumpet = (s, col = '#f2c024', col2 = '#e09a18') => s <= 0.02 ? '' :
    `<g transform="scale(${px(s)})"><path d="M0,2 C -5,-1 -6,-7 -4,-10 C -1,-8 1,-8 4,-10 C 6,-7 5,-1 0,2 Z" fill="${col}"/>
     <path d="M-3,-8 L -1,-2 M3,-8 L 1,-2" stroke="${col2}" stroke-width="1" fill="none"/></g>`;

  // ── 通用发芽期（p < 0.10）：土包 → 芽钩 → 双子叶 ──
  function cotyPair(c, h, open, scale) {
    if (open <= 0.02) return '';
    const s = px(open * scale);
    const one = rot => put(`<ellipse cx="5.5" cy="0" rx="5.5" ry="3.2" fill="${c.col('#8cba72')}"/>
      <path d="M1,0 Q 5.5,-1.2 10,-0.4" stroke="${c.col('#c2dfa8')}" stroke-width="0.9" fill="none" opacity="0.9"/>`,
      0, -h, rot, s);
    return one(-148 - c.droop * 0.4) + one(-32 + c.droop * 0.4);
  }
  function germination(c) {
    const bump = t(c.p, 0, 0.04);
    const g = t(c.p, 0.03, 0.095);
    let s = `<ellipse cx="0" cy="0.5" rx="${px(8 + bump * 3)}" ry="${px(2.6 + bump * 1.2)}" fill="#54402c" opacity="0.8"/>`;
    if (bump > 0.6 && g < 0.35) s += `<path d="M-4,-0.5 L-1.5,-1.8 M0.5,-2.2 L3,-1 M4,-0.5 L6,-1.5"
      stroke="#38291a" stroke-width="1" stroke-linecap="round" opacity="${px(0.9 - g * 2)}"/>`;
    if (g > 0) {
      const h = 2.5 + g * 8;
      const open = t(c.p, 0.062, 0.098);           // 芽钩直立后子叶才展开
      const hook = 1 - t(c.p, 0.045, 0.075);       // 弯着腰破土的芽钩
      s += `<path d="M0,1.5 C 0,${px(-h * 0.5)} ${px(-1.5 - hook * 3)},${px(-h * 0.9)} ${px(-hook * 4.5)},${px(-h)}"
        stroke="${c.col('#7fae67')}" stroke-width="1.9" fill="none" stroke-linecap="round"/>`;
      s += cotyPair(c, h, open, 1.05);
    }
    return s;
  }

  // ═══ 带状叶：葱蒜、韭葱、玉米 ═══
  // 原来是一根描边线（stroke）——放大之后就是一笔一笔的铅笔线，是全场最"草图"的一类。
  // 改成有宽度的实心叶带：基部宽、叶尖收成尖，沿主脉一半受光一半背光，中间一道浅色主脉。
  // 几何：以二次贝塞尔（基部 P0 → 控制点 P1 → 叶尖 P2）为中线，两侧按法线各外扩半个叶宽。
  function blade(x0, y0, x1, y1, x2, y2, w, col, lit = true) {
    const nrm = (dx, dy) => { const L = Math.hypot(dx, dy) || 1; return [-dy / L, dx / L]; };
    const [n0x, n0y] = nrm(x1 - x0, y1 - y0);
    const [n1x, n1y] = nrm(x2 - x0, y2 - y0);
    const h = w / 2, hc = w * 0.62;
    const B1 = [x0 + n0x * h, y0 + n0y * h], B2 = [x0 - n0x * h, y0 - n0y * h];
    const C1 = [x1 + n1x * hc, y1 + n1y * hc], C2 = [x1 - n1x * hc, y1 - n1y * hc];
    const P = a => `${px(a[0])},${px(a[1])}`;
    const tip = P([x2, y2]), mid = P([x1, y1]), base = P([x0, y0]);
    const full = `M${P(B1)} Q ${P(C1)} ${tip} Q ${P(C2)} ${P(B2)} Z`;
    return `<path d="${full}" fill="${col}" stroke="${mixc(col, '#10240c', 0.4)}" stroke-width="0.4" stroke-opacity="0.6"/>`
      + (lit ? `<path d="M${P(B1)} Q ${P(C1)} ${tip} Q ${mid} ${base} Z" fill="#fffbe8" opacity="0.14"/>` : '')
      + `<path d="M${P(B2)} Q ${P(C2)} ${tip} Q ${mid} ${base} Z" fill="#0e2008" opacity="0.12"/>`
      + (w > 1.6 ? `<path d="M${base} Q ${mid} ${tip}" stroke="${mixc(col, '#f4f8e4', 0.35)}" stroke-width="${px(Math.min(0.7, w * 0.16))}" fill="none" opacity="0.7"/>` : '');
  }

  // ═══ 背景叶幕：让直立/攀援作物有"一丛"的体量 ═══
  // 番茄、辣椒、豌豆、黄瓜原来只有几片**可数的叶**（它们驱动"今天多抽了一片叶"的播报，数量不能动），
  // 挂在一根细茎上，放大成主角后像一副骨架——这是"像草图"的另一半原因。
  // 这里在茎后面补一层**不计数**的暗一档叶幕：只随株高长满，不参与 census，画面与播报仍然对得上。
  // 位置按株 id 确定性抖动，每株各不相同、每天打开都一样。
  function backFoliage(c, H, o) {
    const g = t(c.p, o.from || 0.2, o.full || 0.85);
    if (g <= 0.02) return '';
    const n = o.n || 6, dark = c.col2(mixc(c.leafD, '#16300f', 0.32));
    let s = '';
    for (let i = 0; i < n; i++) {
      const k = (i + 0.5) / n;
      const side = i % 2 ? 1 : -1;
      const y = -H * lerp(o.lo || 0.18, o.hi || 0.95, k) + jit(c.id, `bf${i}`, 4);
      const x = (o.x || 0) + side * (1.5 + Math.abs(jit(c.id, `bx${i}`, 4)));
      const rot = (side > 0 ? -18 - 30 * hash(`${c.id}|br${i}`) : -162 + 30 * hash(`${c.id}|br${i}`)) + (side > 0 ? -c.droop : c.droop) * 0.6;
      const art = o.kind === 'compound' ? compound(dark, c.col('#cfe4ae')) : LEAF[o.kind || 'oval'](dark, c.col('#cfe4ae'));
      s += put(art, x, y, rot, (o.size || 0.8) * g * lerp(1.05, 0.72, k));
    }
    return s;
  }

  // ═══ 形态档案 ═══
  // 叶菜莲座：叶片一层层从外向内舒展；upright 直立株型；heart 结球（生菜）
  function rosette(c, cfg) {
    const g = t(c.p, 0.08, 1);
    const overall = lerp(0.4, cfg.size || 1.15, g);
    // 叶序排到 12 片（原来 9 片，0.61 就排完了，后半程画面全静止）
    const angles = cfg.upright
      ? [-90, -122, -58, -142, -38, -108, -72, -152, -28, -98, -82, -134]
      : [-90, -130, -50, -152, -28, -112, -68, -165, -15, -100, -80, -140];
    const n = Math.min(cfg.n || 12, angles.length);
    let s = '';
    for (let i = 0; i < n; i++) {
      const open = seqOpen(c.p, SEQ.rosette, i);
      if (open <= 0) break;
      const size = open * overall * lerp(1.05, 0.6, i / n);   // 外圈老叶大、心叶小
      const rot = angles[i] + (angles[i] > -90 ? 1 : -1) * c.droop * 0.6 + jit(c.id, i, 9);
      const fill = i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4);
      s = put(LEAF[cfg.leaf](c.col2(fill), c.col('#d7e8b8'), c.col('#eef3e0')), 0, -3, rot, size) + s;
    }
    if (cfg.heart) {                                          // 生菜结球（一档一档抱紧）
      const hb = qstep(t(c.p, 0.55, 0.95), 4);
      if (hb > 0.02) {
        const r = hb * 8;
        s += `<circle cx="0" cy="${px(-5 - hb * 3)}" r="${px(r)}" fill="${c.col('#bcd98c')}"/>
          <path d="M${px(-r * 0.7)},${px(-5 - hb * 3 + r * 0.5)} Q 0,${px(-5 - hb * 5.5)} ${px(r * 0.7)},${px(-5 - hb * 3 + r * 0.4)}"
            stroke="${c.col('#9dc46a')}" stroke-width="1.6" fill="none"/>`;
      }
    }
    return s;
  }
  rosette.count = (p, cfg) => ({
    leaf: seqCount(p, SEQ.rosette, Math.min(cfg.n || 12, 12)),
    unfurl: seqUnfurl(p, SEQ.rosette, Math.min(cfg.n || 12, 12)),
    head: cfg.heart ? Math.round(qstep(t(p, 0.55, 0.95), 4) * 4) : 0
  });

  // 根菜：叶丛 + 根肩自 55% 起从土里拱出（globe 圆根 / taper 露肩胡萝卜）
  function rootPlant(c, cfg) {
    const sh = qstep(t(c.p, 0.45, 1), 6);        // 根肩一档一档往外顶，看得见"又拱出来一截"
    let root = '';
    if (sh > 0.02) {
      root = `<ellipse cx="0" cy="2" rx="${px(6 + sh * 5)}" ry="2.6" fill="#43331f" opacity="0.7"/>`; // 顶开的土
      if (cfg.root === 'globe') {
        const r = 2.5 + sh * 6;
        root += `<g transform="translate(0,${px(1 - sh * 3.2)})">
          <circle r="${px(r)}" fill="${c.col(cfg.rootColor)}"/>
          ${cfg.rootTop ? `<path d="M${px(-r * 0.92)},${px(-r * 0.38)} A ${px(r)} ${px(r)} 0 0 1 ${px(r * 0.92)},${px(-r * 0.38)}
             L 0,${px(-r * 0.1)} Z" fill="${c.col(cfg.rootTop)}"/>` : ''}
          <ellipse cx="${px(-r * 0.32)}" cy="${px(-r * 0.3)}" rx="${px(r * 0.3)}" ry="${px(r * 0.2)}" fill="#fff" opacity="0.35"/>
          <path d="M0,${px(r * 0.85)} L 0,${px(r + 2.2)}" stroke="${c.col(cfg.rootColor)}" stroke-width="1" opacity="0.7"/></g>`;
      } else {
        const w = 2.8 + sh * 4;
        root += `<path d="M${px(-w)},2.5 Q 0,${px(-1.5 - sh * 2.6)} ${px(w)},2.5 L ${px(w * 0.62)},5.5 Q 0,7 ${px(-w * 0.62)},5.5 Z"
          fill="${c.col(cfg.rootColor)}"/>
          <path d="M${px(-w * 0.5)},1.5 Q 0,0.4 ${px(w * 0.5)},1.5" stroke="#fff" stroke-width="0.9" opacity="0.3" fill="none"/>`;
      }
    }
    const g = t(c.p, 0.08, 1);
    const overall = lerp(0.45, cfg.size || 1.2, g);
    const angles = [-90, -120, -60, -140, -40, -105, -75, -155, -25];
    let leaves = '';
    for (let i = 0; i < Math.min(cfg.n || 8, angles.length); i++) {
      const open = seqOpen(c.p, SEQ.root, i);
      if (open <= 0) break;
      const rot = angles[i] + (angles[i] > -90 ? 1 : -1) * c.droop * 0.6 + jit(c.id, i, 10);
      const size = open * overall * lerp(1, 0.68, i / 8);
      const art = cfg.top === 'fern' ? LEAF.fern(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.45)))
        : LEAF.oval(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4)), c.col(cfg.vein || '#d7e8b8'));
      leaves = put(art, 0, -4, rot, size) + leaves;
    }
    return root + leaves;
  }
  rootPlant.count = (p, cfg) => ({
    leaf: seqCount(p, SEQ.root, Math.min(cfg.n || 8, 9)),
    unfurl: seqUnfurl(p, SEQ.root, Math.min(cfg.n || 8, 9)),
    root: Math.round(qstep(t(p, 0.45, 1), 6) * 6)
  });

  // 葱蒜：管状/扁平叶直立丛生，鳞茎后期露肩
  function spears(c, cfg) {
    const g = t(c.p, 0.08, 1);
    const n = cfg.n || 7;
    let s = '';
    // 鳞茎画在叶丛**之后**（＝盖在叶片前面）：它才是这一档一档往外顶的可数器官，
    // 画在后面就被十几根叶挡住，玩家看不见"今天又鼓出来一圈"
    const sh = qstep(t(c.p, 0.45, 1), 6);
    let bulb = '';
    if (sh > 0.02) {
      const r = 2 + sh * 4.6;
      bulb = `<ellipse cx="0" cy="1.5" rx="${px(r + 3)}" ry="2.4" fill="#43331f" opacity="0.7"/>
        <g transform="translate(0,${px(1 - sh * 2)})"><ellipse rx="${px(r)}" ry="${px(r * 0.78)}" fill="${c.col(cfg.bulb)}"/>
        <path d="M${px(-r * 0.5)},${px(-r * 0.5)} Q ${px(-r * 0.55)},0 ${px(-r * 0.5)},${px(r * 0.5)}
          M0,${px(-r * 0.72)} Q 0,0 0,${px(r * 0.72)} M${px(r * 0.5)},${px(-r * 0.5)} Q ${px(r * 0.55)},0 ${px(r * 0.5)},${px(r * 0.5)}"
          stroke="#00000022" stroke-width="0.8" fill="none"/></g>`;
    }
    for (let i = 0; i < n; i++) {
      const open = seqOpen(c.p, SEQ.spears, i);
      if (open <= 0) break;
      const lean = (i - (n - 1) / 2) * (cfg.flat ? 12 : 8) + jit(c.id, i, 7);
      const h = open * lerp(10, cfg.h || 34, g) * lerp(0.78, 1.06, hash(c.id + i));
      // 外斜量跟着叶长走：幼苗期第一片叶只有几个单位高，却按成株的外斜量往旁边甩，
      // 画出来是一片平躺在土上的叶（实心叶带之后一眼可见，原来的细线把它藏住了）
      const tip = clamp(lean * 0.9 + (lean >= 0 ? 1 : -1) * c.droop * 0.5, -h * 0.75, h * 0.75);
      const midX = clamp(lean * 0.5, -h * 0.4, h * 0.4);
      s += blade(lean * 0.1, 1.5, midX, -h * 0.55, tip, -h, cfg.flat ? 3.8 : 2.8,
        c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.42)));
    }
    return s + bulb;
  }
  spears.count = (p, cfg) => ({
    leaf: seqCount(p, SEQ.spears, cfg.n || 7),
    unfurl: seqUnfurl(p, SEQ.spears, cfg.n || 7),
    bulb: Math.round(qstep(t(p, 0.45, 1), 6) * 6)
  });

  // 土豆：培土垄上的矮丛，小复叶，中期开小白花，临熟茎叶转黄（该收了）
  function bushlow(c) {
    const g = t(c.p, 0.08, 1);
    const fade = qstep(t(c.p, 0.8, 1), 3) * 0.55;              // 熟前枯黄，分 3 档（＝该收了的信号）
    const leafCol = base => mixc(c.col2(base), '#b0a24e', fade);
    let s = `<ellipse cx="0" cy="2.5" rx="${px(14 + g * 12)}" ry="${px(3.6 + g * 1.6)}" fill="#4a3826" opacity="0.85"/>`;
    const stems = [[-1, -68], [1, -112], [-1, -42], [1, -138], [0, -90], [-1, -125], [1, -55]];
    for (let i = 0; i < 7; i++) {
      const open = seqOpen(c.p, SEQ.bush, i);
      if (open <= 0) break;
      const size = open * lerp(0.6, 1.35, g) * lerp(1, 0.8, i / 7);
      const rot = stems[i][1] + stems[i][0] * c.droop * 0.7 + jit(c.id, i, 12);
      s += put(compound(leafCol(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4)), c.col('#d7e8b8')), 0, -2, rot, size);
    }
    const fpos = [[-6, -20], [7, -23], [1, -26]];              // 花逐朵开、逐朵谢
    bloomSet(c.p, 3, 0.5, 0.64, 0.74).forEach(f =>
      s += put(bloom5(c.col('#f3eefa'), c.col('#e0c24a'), 2.6), fpos[f.i][0], fpos[f.i][1] * g, 0, f.open));
    return s;
  }
  bushlow.count = p => ({
    leaf: seqCount(p, SEQ.bush, 7), unfurl: seqUnfurl(p, SEQ.bush, 7),
    flower: bloomSet(p, 3, 0.5, 0.64, 0.74).length,
    yellow: Math.round(qstep(t(p, 0.8, 1), 3) * 3)
  });

  // 番茄：木支架 + 绑蔓 + 复叶 + 黄花 + 果穗逐颗坐果与转色（青→红）
  function staked(c, cfg) {
    const H = lerp(9, 60, t(c.p, 0.08, 0.92));
    const stakeOn = c.p > 0.28;
    let s = '';
    if (stakeOn) s += `<path d="M7,3 L7,${px(-H - 8)}" stroke="#8a6338" stroke-width="3" stroke-linecap="round"/>
      <path d="M5.5,${px(-H - 7.5)} L8.5,${px(-H - 7.5)}" stroke="#6e4d28" stroke-width="1.6"/>`;
    s += backFoliage(c, H, { kind: 'compound', n: 7, size: 0.95, lo: 0.2, hi: 0.98 });
    s += `<path d="M0,2 C -1.5,${px(-H * 0.35)} 2.5,${px(-H * 0.7)} 1,${px(-H)}"
      stroke="${c.col2('#4a7a38')}" stroke-width="${px(lerp(1.6, 3.2, c.p))}" fill="none" stroke-linecap="round"/>`;
    if (stakeOn) s += `<path d="M1,${px(-H * 0.45)} L7,${px(-H * 0.45 - 1)} M1.5,${px(-H * 0.8)} L7,${px(-H * 0.8 - 1)}"
      stroke="#b89a68" stroke-width="1.1"/>`;                  // 绑蔓绳
    for (let i = 0; i < 5; i++) {
      const open = seqOpen(c.p, SEQ.staked, i);
      if (open <= 0) break;
      const y = -H * (0.24 + i * 0.17);
      const side = i % 2 ? 1 : -1;
      const size = open * lerp(0.7, 1.3, t(c.p, 0.25, 1)) * lerp(1, 0.82, i / 5);
      s += put(compound(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.35)), c.col('#cfe4ae')),
        side * 1.5, y, side > 0 ? -28 - c.droop : -152 + c.droop, size);
    }
    const fpos = [[-8, 0.88], [6, 0.97], [-3, 1.02]];
    bloomSet(c.p, 3, 0.56, 0.68, 0.8).forEach(f =>
      s += put(bloom5(c.col('#f0d24a'), c.col('#c9a428'), 2.4), fpos[f.i][0], -H * fpos[f.i][1], 0, f.open));
    // 三挂果穗，8 颗果逐颗坐果、逐颗转色——一个结果期摊下来几乎每天都数得出不同
    const SLOT = [[-9, 0.42], [-12.6, 0.46], [-6, 0.5], [8, 0.6], [11.4, 0.64], [5, 0.68], [-4, 0.85], [-7.6, 0.88]];
    const fruits = fruitState(c.p, planOf(cfg));
    [[-9, 0.42], [8, 0.6], [-4, 0.85]].forEach(([cx0, cyF], k) => {   // 果柄：该穗上有果才画
      if (!fruits.some(f => Math.floor(f.i / 3) === k)) return;
      s += `<path d="M${px(cx0 * 0.2)},${px(-H * cyF + 1)} Q ${px(cx0 * 0.6)},${px(-H * cyF - 2)} ${px(cx0)},${px(-H * cyF - 4.5)}"
        stroke="${c.col2('#4a7a38')}" stroke-width="1" fill="none"/>`;
    });
    fruits.forEach(f => {
      const [x, yF] = SLOT[f.i];
      const y = -H * yF, r = (1.9 + f.size * 3.4) * vary(c.id, f.i, 0.16);
      // 逐颗色相微偏：同一档转色的两颗也不完全一样，成串时才不像复制粘贴
      const base = c.alive
        ? mixc(mixc('#8fae4f', c.crop.color, f.ripe), f.i % 2 ? '#d8452c' : '#f07a3a', 0.07 * f.ripe)
        : c.col('#9a8f7a');
      s += contact(x, y + r * 0.92, r)
        + orb(x, y, r, base)
        // 萼片：五个小尖角，是番茄一眼可辨的特征，小尺寸下比果形更有识别度
        + `<g transform="translate(${px(x)},${px(y - r * 0.82)})">
            <path d="M-2.4,0.5 L0,-2.2 L2.4,0.5 M-1.4,0.9 L-3.2,-1.2 M1.4,0.9 L3.2,-1.2"
              stroke="${c.col('#5d8a42')}" stroke-width="0.9" fill="none" stroke-linecap="round"/>
            <circle r="0.7" fill="${c.col('#4f7d38')}"/></g>`;
    });
    return s;
  }
  staked.count = (p, cfg) => {
    const fs = fruitState(p, planOf(cfg));
    return { leaf: seqCount(p, SEQ.staked, 5), unfurl: seqUnfurl(p, SEQ.staked, 5), flower: bloomSet(p, 3, 0.56, 0.68, 0.8).length, fruit: fs.length, ripe: ripeCount(fs) };
  };

  // 辣椒：直立小灌木，果实逐个垂挂、逐个转色
  function pepperBush(c, cfg) {
    const H = lerp(8, 42, t(c.p, 0.08, 0.92));
    let s = backFoliage(c, H, { kind: 'oval', n: 8, size: 0.7, lo: 0.35, hi: 1.02 }) + `<path d="M0,2 L 0.5,${px(-H)}" stroke="${c.col2('#4a7a38')}" stroke-width="${px(lerp(1.5, 3, c.p))}" stroke-linecap="round"/>
      <path d="M0,${px(-H * 0.4)} C -6,${px(-H * 0.55)} -9,${px(-H * 0.75)} -10,${px(-H * 0.9)}
        M0.3,${px(-H * 0.5)} C 6,${px(-H * 0.65)} 9,${px(-H * 0.8)} 10,${px(-H * 0.95)}"
        stroke="${c.col2('#4a7a38')}" stroke-width="1.6" fill="none"/>`;
    const spots = [[-10, 0.92, -150], [10, 0.97, -30], [-4, 1.02, -115], [5, 1.05, -60], [-7, 0.72, -160], [8, 0.75, -20]];
    spots.forEach(([x, yF, rot], i) => {
      const open = seqOpen(c.p, SEQ.pepper, i);
      if (open <= 0) return;
      s += put(LEAF.oval(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4)), c.col('#cfe4ae')),
        x * 0.55, -H * yF * 0.92, rot + (rot > -90 ? -c.droop : c.droop), open * 0.75);
    });
    const fpos = [[-5, 0.8], [6, 0.9], [1, 0.96]];
    bloomSet(c.p, 3, 0.56, 0.68, 0.78).forEach(f =>
      s += put(bloom5(c.col('#f6f2e6'), c.col('#d8c24a'), 2), fpos[f.i][0], -H * fpos[f.i][1], 0, f.open));
    const SLOT = [[-8, 0.78], [7, 0.85], [0, 0.95], [-4, 0.66], [10, 0.7]];
    fruitState(c.p, planOf(cfg)).forEach(f => {
      const [x, yF] = SLOT[f.i];
      const fill = c.alive ? mixc('#6f9a42', c.crop.color, f.ripe) : c.col('#9a8f7a');
      const L = 4 + f.size * 8;
      const y = -H * yF;
      const w = 2.6 * vary(c.id, f.i, 0.2);
      const lit = mixc(fill, '#ffffff', 0.3), dark = mixc(fill, '#3a1a08', 0.22);
      const pod = (col, kw, kl) => `<path d="M${px(x - w * kw)},${px(y + 2)} C ${px(x - w * kw * 1.15)},${px(y + 2 + L * kl * 0.7)} ${px(x - w * kw * 0.4)},${px(y + 2 + L * kl)} ${px(x + w * 0.24 * kw)},${px(y + 2 + L * kl)}
          C ${px(x + w * kw * 0.9)},${px(y + 2 + L * kl * 0.85)} ${px(x + w * kw * 0.92)},${px(y + 2 + L * kl * 0.3)} ${px(x + w * kw * 0.76)},${px(y + 2)} Z" fill="${col}"/>`;
      s += `<path d="M${px(x)},${px(y)} L ${px(x)},${px(y + 2)}" stroke="${c.col('#5d8a42')}" stroke-width="1.2"/>
        <path d="M${px(x - 2.2)},${px(y + 1.4)} q 2.2,-1.6 4.4,0" stroke="${c.col('#4f7d38')}" stroke-width="1.1" fill="none"/>
        ${pod(dark, 1, 1)}
        <g transform="translate(-0.35,-0.5)">${pod(fill, 0.88, 0.94)}</g>
        <g transform="translate(-0.85,-0.9)">${pod(lit, 0.34, 0.66)}</g>`;
    });
    return s;
  }
  pepperBush.count = (p, cfg) => {
    const fs = fruitState(p, planOf(cfg));
    return { leaf: seqCount(p, SEQ.pepper, 6), unfurl: seqUnfurl(p, SEQ.pepper, 6), flower: bloomSet(p, 3, 0.56, 0.68, 0.78).length, fruit: fs.length, ripe: ripeCount(fs) };
  };

  // 攀援棚架（黄瓜/豌豆/四季豆）：A 字竹架 + 卷须 + 各自的果
  function trellis(c, cfg) {
    const G = t(c.p, 0.08, 1);
    const H = lerp(8, 54, t(c.p, 0.08, 0.9));
    let s = '';
    if (c.p > 0.2) s += `<g stroke="#a8845a" stroke-width="2.4" stroke-linecap="round">
      <path d="M-14,3 L-3,${px(-56)}"/><path d="M14,3 L3,${px(-56)}"/>
      <path d="M-3.6,${px(-51)} Q 0,${px(-54)} 3.6,${px(-51)}" fill="none" stroke-width="1.6"/></g>`;
    // 主蔓沿左杆盘绕而上
    const nodes = [];
    const seg = 5;
    let d = `M-9,2`;
    for (let i = 1; i <= seg; i++) {
      const yy = -H * (i / seg);
      const xx = -9 + (i / seg) * 6.5 + Math.sin(i * 2.6) * 3.4;
      nodes.push([xx, yy]);
      d += ` Q ${px(xx + Math.cos(i) * 3)},${px(yy + H / seg * 0.5)} ${px(xx)},${px(yy)}`;
    }
    s = s + backFoliage(c, H, { kind: cfg.bigLeaf ? 'lobed' : 'oval', n: cfg.bigLeaf ? 5 : 7, size: cfg.bigLeaf ? 0.75 : 0.58, x: -5, lo: 0.15, hi: 0.95 });
    s += `<path d="${d}" stroke="${c.col2('#4d8040')}" stroke-width="${px(lerp(1.4, 2.4, G))}" fill="none"/>`;
    nodes.forEach(([x, y], i) => {
      const open = seqOpen(c.p, SEQ.vine, i);
      if (open <= 0) return;
      const side = i % 2 ? 1 : -1;
      const art = cfg.bigLeaf
        ? LEAF.lobed(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.35)), c.col('#cfe4ae'))
        : LEAF.oval(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4)), c.col('#d7e8b8'));
      s += put(art, x + side * 1.5, y, side > 0 ? -30 - c.droop : -150 + c.droop, open * (cfg.bigLeaf ? 0.85 : 0.62));
      if (open > 0.6) s += `<path d="M${px(x)},${px(y)} q ${px(3 * side)},-2.5 ${px(2 * side)},-5 q ${px(-side)},-2 ${px(-2.6 * side)},-1.2"
        stroke="${c.col('#6a9a52')}" stroke-width="0.9" fill="none"/>`;   // 卷须
    });
    const FPOS = [[2, 6], [3, -6], [1, 6]];
    bloomSet(c.p, 3, 0.53, 0.66, 0.78).forEach(f => {
      const [ni, dx] = FPOS[f.i];
      const fart = cfg.flower === 'trumpet' ? trumpet(f.open * 0.9)
        : put(bloom5(c.col(cfg.flower || '#f6f2ea'), c.col('#d8c24a'), 2.2), 0, 0, 0, f.open);
      s += put(fart, nodes[ni] ? nodes[ni][0] + dx : dx, nodes[ni] ? nodes[ni][1] : -H * 0.7, 0, 1);
    });
    // 果实挂在蔓节上：逐个坐果、逐个长成（豆荚一根一根多出来）
    const SLOT = [[1, -0.7], [3, 0.7], [2, -0.7], [4, 0.7], [1, 0.9], [3, -0.9]];
    fruitState(c.p, planOf(cfg)).forEach(f => {
      const [ni, off] = SLOT[f.i % SLOT.length];
      if (!nodes[ni]) return;
      const grow = f.size, k = f.i;
      const [x, y] = nodes[ni];
      const hx = x + off * 5, hy = y + 2 + (f.i >= 4 ? 3 : 0);
      if (cfg.fruit === 'cuke') {
        const L = grow * 15;
        s += `<path d="M${px(hx)},${px(hy)} q 0.5,2 0,3" stroke="${c.col('#5d8a42')}" stroke-width="1" fill="none"/>
          <path d="M${px(hx - 1.9)},${px(hy + 3)} C ${px(hx - 2.6)},${px(hy + 3 + L * 0.7)} ${px(hx - 1.6)},${px(hy + 3 + L)} ${px(hx + 0.2)},${px(hy + 3 + L)}
            C ${px(hx + 2.4)},${px(hy + 3 + L * 0.96)} ${px(hx + 2.4)},${px(hy + 3 + L * 0.25)} ${px(hx + 1.9)},${px(hy + 3)} Z"
            fill="${c.col(k % 2 ? '#3f7030' : '#487c36')}"/>
          <path d="M${px(hx - 0.6)},${px(hy + 4.5)} L ${px(hx - 0.8)},${px(hy + 1.5 + L)}" stroke="#e8f0d0" stroke-width="0.7" opacity="0.5"/>`;
      } else if (cfg.fruit === 'pea') {
        // 一个坐果位＝一根荚：豆荚一根一根多出来，且鼓粒（内里 3 颗豆）随膨大档位显影
        const L = 5 + grow * 8, dx = (k % 2 ? 3 : -2.5), dy = (k % 2 ? 1.5 : 0);
        s += `<path d="M${px(hx + dx)},${px(hy + dy)} C ${px(hx + dx - 3)},${px(hy + dy + L * 0.7)} ${px(hx + dx - 1)},${px(hy + dy + L)} ${px(hx + dx + 1)},${px(hy + dy + L * 0.9)}
            C ${px(hx + dx + 2.4)},${px(hy + dy + L * 0.6)} ${px(hx + dx + 1.8)},${px(hy + dy + L * 0.2)} ${px(hx + dx)},${px(hy + dy)} Z"
            fill="${c.col(mixc('#b6d98a', '#8fc95e', f.ripe))}" stroke="${c.col('#4f7f3a')}" stroke-width="0.5"/>
           ${[0.3, 0.55, 0.78].slice(0, 1 + Math.round(f.ripe * 2)).map(q =>
             `<circle cx="${px(hx + dx - 0.6 - q)}" cy="${px(hy + dy + L * q)}" r="0.9" fill="${c.col('#5d9440')}"/>`).join('')}`;
      } else {  // 四季豆：一个坐果位＝一根细长豆荚
        const L = 6 + grow * 9, dx = [-3, 0.5, 3.5][k % 3], ang = [-14, 4, 16][k % 3];
        s += `<path d="M${px(hx + dx)},${px(hy)} q ${px(Math.sin(ang / 57) * L * 0.4 - 1)},${px(L * 0.55)} ${px(Math.sin(ang / 57) * L * 0.7)},${px(L)}"
            stroke="${c.col(mixc('#7cb86a', '#5a9648', f.ripe))}" stroke-width="1.7" fill="none" stroke-linecap="round"/>`;
      }
    });
    return s;
  }
  trellis.count = (p, cfg) => {
    const fs = fruitState(p, planOf(cfg));
    return { leaf: seqCount(p, SEQ.vine, 5), unfurl: seqUnfurl(p, SEQ.vine, 5), flower: bloomSet(p, 3, 0.53, 0.66, 0.78).length, fruit: fs.length, ripe: ripeCount(fs) };
  };

  // 匍匐大瓜（西葫芦/南瓜）：大裂叶带白斑纹 + 大黄花 + 地上结大果
  function sprawl(c, cfg) {
    const G = t(c.p, 0.08, 1);
    let s = `<path d="M-2,1 Q -14,0 -24,3 M0,1 Q 12,-1 24,2.5" stroke="${c.col2('#4d8040')}"
      stroke-width="${px(lerp(1.2, 2.4, G))}" fill="none"/>`;   // 地面爬蔓
    const spots = [[-6, -8, -118], [6, -8, -62], [-14, -5, -145], [14, -5, -35], [0, -10, -90], [-20, -2, -160], [20, -2, -20]];
    spots.forEach(([x, y, rot], i) => {
      const open = seqOpen(c.p, SEQ.sprawl, i);
      if (open <= 0) return;
      const size = open * lerp(0.55, 1.5, G) * lerp(1, 0.72, i / 7);
      const fill = c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.35));
      s += put(`${LEAF.lobed(fill, c.col('#e6efd2'))}`, x, y, rot + (rot > -90 ? -c.droop : c.droop) * 0.5, size);
    });
    const fpos = [[16, -16, 20], [-18, -12, -15], [2, -18, 5]];
    bloomSet(c.p, 3, 0.5, 0.64, 0.76).forEach(f =>
      s += put(trumpet(1, c.col('#f2c024'), c.col('#e09a18')), fpos[f.i][0], fpos[f.i][1] * G, fpos[f.i][2], f.open));
    // 大瓜逐个坐果：南瓜个头大、只结两个（膨大与转橙各分 3 档，天天都在"再大一圈"）
    const SLOT = [24, -26, 6, -8];
    fruitState(c.p, planOf(cfg)).forEach(f => {
      const cx0 = SLOT[f.i % SLOT.length];
      if (cfg.fruit === 'pumpkin') {
        const r = (3 + f.size * 11.5) * vary(c.id, f.i, 0.12);
        const fill = c.alive ? mixc('#7a9440', '#e07b28', f.ripe) : c.col('#9a8f7a');
        s += contact(cx0, 2.4, r * 0.95)
          + `<g transform="translate(${px(cx0)},${px(-r * 0.55 + 2)})">
          ${orb(0, 0, r, fill, { flat: 0.8 })}
          ${[-0.62, -0.3, 0.3, 0.62].map(q => `<path d="M${px(r * q)},${px(-r * 0.72)} Q ${px(r * q * 1.4)},0 ${px(r * q)},${px(r * 0.72)}"
             stroke="#00000022" stroke-width="1.1" fill="none"/>`).join('')}
          <path d="M-1.5,${px(-r * 0.8)} Q 0,${px(-r * 0.8 - 3)} 2.5,${px(-r * 0.8 - 2)}" stroke="${c.col2('#5d7a34')}" stroke-width="2.4" fill="none" stroke-linecap="round"/></g>`;
      } else {
        const L = 6 + f.size * 19, dir = cx0 >= 0 ? 1 : -1;
        s += `<g transform="translate(${px(cx0 - dir * L * 0.4)},${px(f.i >= 2 ? -4 : 0)}) scale(${dir},1)">
          <path d="M0,0.5 C 1,-2.6 ${px(L * 0.8)},-3.4 ${px(L)},-1.2 C ${px(L + 2)},0.4 ${px(L + 1)},2.2 ${px(L - 1)},2.4 C ${px(L * 0.4)},3 1,2.6 0,0.5 Z"
            fill="${c.col(mixc('#6fa04a', '#4d8a35', f.ripe))}" stroke="${c.col('#375f28')}" stroke-width="0.6"/>
          <path d="M3,0 L ${px(L - 2)},-0.6 M4,1.6 L ${px(L - 3)},1.2" stroke="#dce8c0" stroke-width="0.7" opacity="0.55"/>
          <circle cx="${px(L + 0.6)}" cy="0.6" r="1.1" fill="${c.col('#e0b13e')}"/></g>`;
      }
    });
    return s;
  }
  sprawl.count = (p, cfg) => {
    const fs = fruitState(p, planOf(cfg));
    return { leaf: seqCount(p, SEQ.sprawl, 7), unfurl: seqUnfurl(p, SEQ.sprawl, 7), flower: bloomSet(p, 3, 0.5, 0.64, 0.76).length, fruit: fs.length, ripe: ripeCount(fs) };
  };

  // 甜玉米：拔节长秆、披针叶下垂、顶端抽雄穗、腰间逐个结穗吐丝
  function cornStalk(c, cfg) {
    const H = lerp(10, 76, t(c.p, 0.08, 0.9));
    let s = `<path d="M0,2 L 0,${px(-H)}" stroke="${c.col2('#5d8a42')}" stroke-width="${px(lerp(1.8, 3.4, c.p))}" stroke-linecap="round"/>
      <path d="M-0.6,2 L -0.6,${px(-H)}" stroke="#fffbe8" stroke-opacity="0.18" stroke-width="${px(lerp(0.6, 1.1, c.p))}"/>`
      // 茎节：玉米秆一眼可认的特征
      + [0.16, 0.31, 0.46, 0.61, 0.76].map(k => `<path d="M-1.6,${px(-H * k)} L 1.6,${px(-H * k)}" stroke="${c.col2('#46702f')}" stroke-width="0.8"/>`).join('');
    for (let i = 0; i < 6; i++) {
      const open = seqOpen(c.p, SEQ.corn, i);
      if (open <= 0) break;
      const side = i % 2 ? 1 : -1;
      const y = -H * (0.16 + i * 0.15);
      const L = open * lerp(10, 26, t(c.p, 0.2, 1)) * lerp(1, 0.8, i / 6);
      const dr = c.droop * 0.25;
      // 玉米叶：长带状、中段拱起再垂下，实心叶带（原来是一根描边线）
      s += blade(0, y, side * L * 0.62, y - L * 0.5, side * L, y + L * 0.12 + dr, 4.2 * open,
        c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4)));
    }
    const tas = qstep(t(c.p, 0.6, 0.72), 3);                    // 雄穗一档一档抽出来
    if (tas > 0.02) s += `<g stroke="${c.col('#d8c05a')}" stroke-width="1.1" fill="none" stroke-linecap="round"
      transform="translate(0,${px(-H)}) scale(${px(tas)})">
      <path d="M0,0 L0,-8 M0,-2 L-5,-9 M0,-2 L5,-9 M0,-3.5 L-2.5,-10 M0,-3.5 L2.5,-10"/></g>`;
    const SLOT = [[3.6, 0.45, 14], [-3.6, 0.62, -14]];          // 一株结两穗，先下后上
    fruitState(c.p, planOf(cfg)).forEach(f => {
      const [x, yF, rot] = SLOT[f.i % SLOT.length];
      const silk = c.alive ? mixc('#e8c88a', '#a4622a', f.ripe) : c.col('#9a8f7a');
      s += `<g transform="translate(${px(x)},${px(-H * yF)}) rotate(${rot}) scale(${px(f.size * 1.3)})">
        <path d="M0,6 C -3,2 -3,-6 0,-9 C 3,-6 3,2 0,6 Z" fill="${c.col2('#6a9a50')}"/>
        <path d="M-1,-4 L -1,3 M1,-4 L 1,3" stroke="${c.col('#8ab86a')}" stroke-width="0.8"/>
        <path d="M-1.2,-8 C -2,-11 -0.5,-12 0,-10.5 M0.2,-8.5 C 0.5,-11.5 2,-12 1.8,-10" stroke="${silk}" stroke-width="1" fill="none"/></g>`;
    });
    return s;
  }
  cornStalk.count = (p, cfg) => {
    const fs = fruitState(p, planOf(cfg));
    return { leaf: seqCount(p, SEQ.corn, 6), unfurl: seqUnfurl(p, SEQ.corn, 6), fruit: fs.length, ripe: ripeCount(fs), tassel: Math.round(qstep(t(p, 0.6, 0.72), 3) * 3) };
  };

  // 草莓：贴地三出复叶丛，白花点点，垂果沿边转红
  function strawberryPlant(c, cfg) {
    const G = t(c.p, 0.08, 1);
    const tri = fill => `<path d="M0,0 Q 5,-1 8,-2.5" stroke="${fill}" stroke-width="1.2" fill="none"/>
      ${[[8, -3.5, -95, 0.5], [7, -2.5, -35, 0.48], [9, -2.8, -150, 0.48]].map(([x, y, r, sc]) =>
        put(`<ellipse cx="4.5" cy="0" rx="4.8" ry="3" fill="${fill}"/>
          <path d="M1,0 L8.5,0" stroke="#cde4ae" stroke-width="0.7" opacity="0.8"/>`, x, y, r, sc)).join('')}`;
    let s = '';
    const spots = [[-1, -95], [1, -55], [-1, -130], [1, -22], [0, -78], [-1, -112], [1, -40]];
    spots.forEach(([sd, rot], i) => {
      const open = seqOpen(c.p, SEQ.berry, i);
      if (open <= 0) return;
      s += put(tri(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4))), 0, -3,
        rot + sd * c.droop * 0.5 + jit(c.id, i, 8), open * lerp(0.6, 1.15, G));
    });
    const fpos = [[-8, -13], [9, -11], [2, -16], [-3, -9]];
    bloomSet(c.p, 4, 0.4, 0.56, 0.68).forEach(f =>
      s += put(bloom5(c.col('#faf6ee'), c.col('#e8c93a'), 2.4), fpos[f.i][0], fpos[f.i][1] * G, 0, f.open));
    // 一颗一颗坐果、一颗一颗转红——草莓是全作物里"今天多了一颗红的"最直观的一种
    const SLOT = [-13, 12, -5, 7, -17, 16];
    fruitState(c.p, planOf(cfg)).forEach(f => {
      const x = SLOT[f.i % SLOT.length];
      const base = c.alive ? mixc('#9ab060', c.crop.color, f.ripe) : c.col('#9a8f7a');
      const lit = mixc(base, '#ffffff', 0.3), dark = mixc(base, '#3a1010', 0.2);
      const r = (1.4 + f.size * 3) * vary(c.id, f.i, 0.18);
      const heart = (fill, k) => `<path d="M${px(-r * k)},${px(-r * 0.4 * k)} C ${px(-r * k)},${px(r * 0.5 * k)} ${px(-r * 0.4 * k)},${px(r * k)} 0,${px(r * k)}
          C ${px(r * 0.4 * k)},${px(r * k)} ${px(r * k)},${px(r * 0.5 * k)} ${px(r * k)},${px(-r * 0.4 * k)}
          C ${px(r * 0.6 * k)},${px(-r * 0.9 * k)} ${px(-r * 0.6 * k)},${px(-r * 0.9 * k)} ${px(-r * k)},${px(-r * 0.4 * k)} Z" fill="${fill}"/>`;
      s += contact(x, r * 0.62, r)
        + `<g transform="translate(${px(x)},${px(-r * 0.4)})">
        ${heart(dark, 1)}
        <g transform="translate(${px(-r * 0.08)},${px(-r * 0.1)})">${heart(base, 0.88)}</g>
        <g transform="translate(${px(-r * 0.3)},${px(-r * 0.32)})">${heart(lit, 0.44)}</g>
        ${[[-r * 0.42, 0], [r * 0.36, -r * 0.18], [0, r * 0.42], [-r * 0.2, r * 0.2], [r * 0.2, r * 0.34]].map(([dx, dy]) =>
          `<circle cx="${px(dx)}" cy="${px(dy)}" r="${px(r * 0.13)}" fill="#f8f0c8" opacity="0.9"/>`).join('')}
        <path d="M${px(-r * 0.62)},${px(-r * 0.72)} L${px(-r * 0.2)},${px(-r * 1.1)} L0,${px(-r * 0.78)}
                 L${px(r * 0.2)},${px(-r * 1.1)} L${px(r * 0.62)},${px(-r * 0.72)}"
          stroke="${c.col('#5d8a42')}" stroke-width="0.9" fill="none" stroke-linejoin="round"/></g>`;
    });
    return s;
  }
  strawberryPlant.count = (p, cfg) => {
    const fs = fruitState(p, planOf(cfg));
    return { leaf: seqCount(p, SEQ.berry, 7), unfurl: seqUnfurl(p, SEQ.berry, 7), flower: bloomSet(p, 4, 0.4, 0.56, 0.68).length, fruit: fs.length, ripe: ripeCount(fs) };
  };

  // 向日葵：粗壮独秆、心形锯齿叶、大花盘（花瓣两轮 + 螺旋盘芯），熟时头微垂
  function sunflowerPlant(c) {
    const H = lerp(10, 70, t(c.p, 0.08, 0.9));
    let s = `<path d="M0,2 C -1,${px(-H * 0.4)} 1,${px(-H * 0.75)} 0,${px(-H)}"
      stroke="${c.col2('#5d8a42')}" stroke-width="${px(lerp(1.8, 4, c.p))}" fill="none" stroke-linecap="round"/>`;
    for (let i = 0; i < 6; i++) {
      const open = seqOpen(c.p, SEQ.sunfl, i);
      if (open <= 0) break;
      const side = i % 2 ? 1 : -1;
      const y = -H * (0.18 + i * 0.13);
      s += put(LEAF.heart(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.35)), c.col('#cfe4ae')),
        side * 1.5, y, side > 0 ? -32 - c.droop : -148 + c.droop, open * lerp(0.6, 1.2, t(c.p, 0.25, 1)) * lerp(1, 0.8, i / 6));
    }
    // 花盘开放分 4 档，花瓣一轮一轮多出来（4 → 16 片）：这是向日葵的"可数器官"
    const bud = qstep(t(c.p, 0.6, 0.78), 3), bloomK = qstep(t(c.p, 0.78, 1), 4);
    if (bloomK > 0.02) {
      const R = lerp(4, 13, bloomK);
      const nod = 8 * t(c.p, 0.9, 1) + c.droop * 0.4;          // 成熟花盘沉甸甸微垂
      const rays = n => Array.from({ length: n }, (_, i) =>
        `<ellipse cx="0" cy="${px(-R * 0.78)}" rx="${px(R * 0.16)}" ry="${px(R * 0.42)}"
          fill="${c.col(i % 2 ? '#f0c419' : '#e8ae10')}" transform="rotate(${i * 360 / n + (i % 2 ? 9 : 0)})"/>`).join('');
      s += `<g transform="translate(0,${px(-H - R * 0.55)}) rotate(${px(nod)})">
        ${rays(4 + Math.round(bloomK * 12))}
        <circle r="${px(R * 0.46)}" fill="${c.col('#6b4423')}"/>
        <circle r="${px(R * 0.3)}" fill="${c.col('#59371c')}"/>
        ${[0, 60, 120, 180, 240, 300].map(a => `<circle cx="${px(R * 0.2 * Math.cos(a / 57.3))}" cy="${px(R * 0.2 * Math.sin(a / 57.3))}"
          r="${px(R * 0.045)}" fill="#8a6636"/>`).join('')}</g>`;
    } else if (bud > 0.02) {
      s += `<g transform="translate(0,${px(-H - 2)}) scale(${px(bud)})">
        <circle r="4.5" fill="${c.col2('#5d8a42')}"/>
        ${[-40, 0, 40, 180, 140, 220].map(a => `<ellipse cx="0" cy="-4" rx="1.6" ry="3.4" fill="${c.col('#6f9a50')}" transform="rotate(${a})"/>`).join('')}</g>`;
    }
    return s;
  }
  sunflowerPlant.count = p => ({
    leaf: seqCount(p, SEQ.sunfl, 6), unfurl: seqUnfurl(p, SEQ.sunfl, 6),
    petal: p >= 0.78 ? 4 + Math.round(qstep(t(p, 0.78, 1), 4) * 12) : 0,
    bud: p >= 0.6 && p < 0.78 ? Math.round(qstep(t(p, 0.6, 0.78), 3) * 3) : 0
  });

  // 万寿菊：细羽叶灌丛 + 一茬茬橙色绒球
  function marigoldPlant(c) {
    const G = t(c.p, 0.08, 1);
    let s = '';
    const sprigs = [[-1, -95], [1, -60], [-1, -125], [1, -30], [0, -78], [-1, -108], [1, -45], [0, -90]];
    sprigs.forEach(([sd, rot], i) => {
      const open = seqOpen(c.p, SEQ.marigold, i);
      if (open <= 0) return;
      s += put(LEAF.fern(c.col2(i % 2 ? c.leafD : mixc(c.leafD, c.leafL, 0.4))), 0, -2,
        rot + sd * c.droop * 0.5 + jit(c.id, i, 10), open * lerp(0.6, 1.2, G));
    });
    const pom = (r, col) => `<circle r="${px(r)}" fill="${col}"/>
      ${[0, 60, 120, 180, 240, 300].map(a => `<circle cx="${px(r * 0.72 * Math.cos(a / 57.3))}"
        cy="${px(r * 0.72 * Math.sin(a / 57.3))}" r="${px(r * 0.42)}" fill="${col}"/>`).join('')}
      <circle r="${px(r * 0.5)}" fill="#fff" opacity="0.22"/>`;
    const cols = ['#e8955a', '#e0793a', '#f0b04a', '#e8955a', '#d86a2e'];
    const gpos = [[-10, -14], [8, -17], [0, -22], [-4, -10], [12, -9]];
    bloomSet(c.p, 5, 0.58, 0.82).forEach(f => {                 // 绒球一朵一朵开出来
      const [x, y] = gpos[f.i];
      s += `<path d="M${px(x * 0.4)},-4 Q ${px(x * 0.8)},${px(y * 0.7)} ${px(x)},${px(y * G)}"
        stroke="${c.col2('#4d7a3a')}" stroke-width="1.1" fill="none"/>`
        + put(pom(4.3, c.col(cols[f.i])), x, y * G, 0, f.open);
    });
    return s;
  }
  marigoldPlant.count = p => ({ leaf: seqCount(p, SEQ.marigold, 8), unfurl: seqUnfurl(p, SEQ.marigold, 8), flower: bloomSet(p, 5, 0.58, 0.82).length });

  // 窗台系作物：直接长在床土里（床即容器，不再套花盆）
  function herbPatch(c, cfg) {
    const G = t(c.p, 0.06, 1);
    let s = '';
    if (cfg.herb === 'micro') {            // 芽苗菜：条播两行，齐刷刷的小苗一天天变密变高
      s += `<path d="M-22,0.5 L22,0.5 M-20,3 L20,3" stroke="#43331f" stroke-width="1.6" opacity="0.6"/>`;
      const n = Math.round(5 + G * 25);
      for (let i = 0; i < n; i++) {
        const row = i % 2;
        const x = -20 + (Math.floor(i / 2) / (Math.ceil(n / 2) - 1 || 1)) * 40 + jit(c.id, i, 4);
        const y = row ? 2.5 : 0;
        const h = (4 + hash(c.id + 'h' + i) * 4) * lerp(0.6, 1.6, G);
        const lean = jit(c.id, 'l' + i, 12);
        s += `<path d="M${px(x)},${px(y)} q ${px(lean * 0.1)},${px(-h * 0.6)} ${px(lean * 0.16)},${px(-h)}"
          stroke="${c.col('#7fb35e')}" stroke-width="1.4" fill="none" stroke-linecap="round"/>
          <ellipse cx="${px(x + lean * 0.16 - 1.7)}" cy="${px(y - h)}" rx="1.9" ry="1.1" fill="${c.col('#8cba72')}"/>
          <ellipse cx="${px(x + lean * 0.16 + 1.7)}" cy="${px(y - h)}" rx="1.9" ry="1.1" fill="${c.col('#9cc681')}"/>`;
      }
    } else if (cfg.herb === 'spears') {    // 香葱：丛生细管叶，成熟后有几根"剪过又长"的平口
      const n = Math.round(5 + G * 20);
      for (let i = 0; i < n; i++) {
        const lean = (i - n / 2) * 2.1 + jit(c.id, i, 5);
        const cut = G > 0.6 && hash(c.id + 'c' + i) < 0.22;
        const h = (cut ? 12 : 16 + hash(c.id + 'h' + i) * 12) * lerp(0.5, 1.6, G);
        // 管状叶：实心细叶带（剪过的那几根叶尖是平口，画成顶端留宽）
        const tx = lean + c.droop * (lean > 0 ? 0.3 : -0.3);
        s += blade(lean * 0.2, 1.5, lean * 0.6, -h * 0.6, tx, -h, 2.2, c.col(i % 2 ? '#5a9650' : '#6daa60'))
          + (cut ? `<ellipse cx="${px(tx)}" cy="${px(-h)}" rx="1" ry="0.5" fill="${c.col('#b8d8a0')}"/>` : '');
      }
    } else if (cfg.herb === 'round') {     // 薄荷：匍匐枝四下蔓延的一小片，圆齿叶
      const stems = [[-1, -125], [1, -55], [-1, -155], [1, -25], [0, -90], [-1, -105], [1, -72], [-1, -140], [1, -40]];
      const nodes = 1 + Math.round(qstep(t(c.p, 0.15, 0.95), 2) * 2);   // 每条枝的叶节数 1→3
      stems.forEach(([sd, rot], i) => {
        const open = seqOpen(c.p, HERB_SEQ.round, i);
        if (open <= 0) return;
        const rr = rot + sd * c.droop * 0.5 + jit(c.id, i, 8);
        s += put(`<path d="M0,0 Q 9,-1.5 17,-2" stroke="${c.col2('#4d8a52')}" stroke-width="1.3" fill="none"/>
          ${[[7, -1, 0.5], [12.5, -1.8, 0.58], [17.5, -2.2, 0.66]].slice(0, nodes).map(([x, y, sc]) =>
            put(`<circle cx="0.4" cy="0.8" r="3.6" fill="${mixc(c.col(i % 2 ? '#3f9e6e' : '#4fae7c'), '#12301c', 0.35)}"/>
              <circle r="3.6" fill="${c.col(i % 2 ? '#3f9e6e' : '#4fae7c')}" ${rim(c.col('#3f9e6e'))}/>
              <path d="M-3.6,0 A 3.6,3.6 0 0 1 3.6,0 Q 0,-1.3 -3.6,0 Z" fill="#fffbe8" opacity="0.14"/>
              <path d="M-3.6,0 Q 0,-1.3 3.6,0 A 3.6,3.6 0 0 1 -3.6,0 Z" fill="#0e2008" opacity="0.13"/>
              <path d="M-2.8,0 Q 0,-1.3 2.8,0" stroke="${mixc(c.col('#3f9e6e'), '#cfeedd', 0.5)}" stroke-width="0.6" fill="none"/>
              <path d="M-3.2,-1 L-3.9,-1.9 M-1,-3.2 L-1.2,-4.2 M1.5,-3 L1.9,-4 M3.2,-0.9 L4,-1.5"
                stroke="${c.col(i % 2 ? '#3f9e6e' : '#4fae7c')}" stroke-width="0.8"/>`, x, y, 0, sc)).join('')}`,
          0, -2, rr, open * lerp(0.55, 1.25, G));
      });
    } else {                               // 罗勒：多秆小灌丛，对生亮泽卵叶
      const stems = [[-6, -14], [0, 0], [6, 14]];
      stems.forEach(([xo, lean], k) => {
        const openS = seqOpen(c.p, HERB_SEQ.stem, k);
        if (openS <= 0) return;
        const h = openS * lerp(5, 27, G) * (k === 1 ? 1.1 : 0.85);
        s += `<g transform="translate(${xo},0) rotate(${lean})">
          <path d="M0,1 L 0,${px(-h)}" stroke="${c.col2('#4a8c3f')}" stroke-width="1.6" stroke-linecap="round"/>`;
        for (let i = 0; i < 4; i++) {
          const open = seqOpen(c.p, HERB_SEQ.bushy, k + i * 1.8);
          if (open <= 0) break;
          const y = -h * (0.3 + i * 0.22);
          const size = open * lerp(0.55, 0.95, G) * lerp(1.05, 0.72, i / 4);
          // 罗勒叶：与通用叶片同一套受光/背光/描边（见 leafBody），外加一块亮泽高光——罗勒叶是出了名的"油亮"
          const bd = c.col(i % 2 ? '#3f7e36' : '#4c9140');
          const glossy = leafBody('M0,0 C 4,-6 12,-7 16,-2 C 12,3 4,3 0,0 Z',
              'M0,0 C 4,-6 12,-7 16,-2 C 11,-2.6 5,-2 0,0 Z', 'M0,0 C 5,-2 11,-2.6 16,-2 C 12,3 4,3 0,0 Z',
              bd, 'M1.5,-0.5 C 7,-2.4 12,-2.6 15,-1.8', veinCol(bd, c.col('#cfe4ae')))
            + `<ellipse cx="6.4" cy="-3" rx="2.8" ry="0.9" fill="#fff" opacity="0.3" transform="rotate(-14 6.4 -3)"/>`;
          s += put(glossy, 0, y, -145 + c.droop * 0.5, size) + put(glossy, 0, y, -35 - c.droop * 0.5, size * 0.95);
        }
        s += '</g>';
      });
    }
    return s;
  }
  herbPatch.count = (p, cfg) => {
    const G = t(p, 0.06, 1);
    if (cfg.herb === 'micro') return { sprout: Math.round(5 + G * 25) };      // 苗数本来就是整数，天天多几株
    if (cfg.herb === 'spears') return { leaf: Math.round(5 + G * 20) };
    if (cfg.herb === 'round') return {
      leaf: seqCount(p, HERB_SEQ.round, 9) * (1 + Math.round(qstep(t(p, 0.15, 0.95), 2) * 2)),
      unfurl: seqUnfurl(p, HERB_SEQ.round, 9)
    };
    let n = 0;
    for (let k = 0; k < 3; k++) for (let i = 0; i < 4; i++)
      if (p >= HERB_SEQ.bushy.start + (k + i * 1.8) * HERB_SEQ.bushy.step) n += 2;
    return { leaf: n, unfurl: Math.round([0,1,2].reduce((a,k)=>a+[0,1,2,3].reduce((b,i)=>b+seqOpen(p,HERB_SEQ.bushy,k+i*1.8)*LEAF_STEPS,0),0)) };
  };

  // ═══ 品种档案表 ═══
  const STYLES = {
    lettuce: { arch: rosette, leaf: 'round', heart: true, size: 1.2, leafD: '#7cab52', leafL: '#a9cf7e' },
    pakchoi: { arch: rosette, leaf: 'spoon', upright: true, leafD: '#4f8a3d', leafL: '#79b25a' },
    spinach: { arch: rosette, leaf: 'oval', upright: true, size: 1.1, leafD: '#2f6231', leafL: '#4d8a4a' },
    kale: { arch: rosette, leaf: 'curly', upright: true, size: 1.15, leafD: '#46755b', leafL: '#6a9a80' },
    radish: { arch: rootPlant, top: 'broad', root: 'globe', rootColor: '#d1495b', n: 8, leafD: '#4c8040', leafL: '#74a862' },
    carrot: { arch: rootPlant, top: 'fern', root: 'taper', rootColor: '#ef6c00', n: 9, size: 1.05, leafD: '#3e7534', leafL: '#5f9a50' },
    beet: { arch: rootPlant, top: 'broad', root: 'globe', rootColor: '#a52a4d', rootTop: '#7e2140', n: 8, vein: '#d86a88', leafD: '#3e6b35', leafL: '#5d8f50' },
    turnip: { arch: rootPlant, top: 'broad', root: 'globe', rootColor: '#ece6f0', rootTop: '#b06ab8', n: 8, leafD: '#4c8040', leafL: '#74a862' },
    potato: { arch: bushlow, leafD: '#456f36', leafL: '#68985a' },
    garlic: { arch: spears, flat: true, n: 7, h: 36, bulb: '#e8e0d0', leafD: '#5a7d4a', leafL: '#7ba268' },
    onion: { arch: spears, n: 9, h: 36, bulb: '#c98b4e', leafD: '#4f7d44', leafL: '#6fa462' },
    // fruits = 坐果排产表 {n 果数, from/to 逐颗坐果的进度区间, ripe 每颗转色所需进度}
    // 调这张表就是在调"结果期每天能看到多少变化"——n 越大、区间越长，日更感越强，
    // 但别超过画稿里的 SLOT 位置数（超了会绕回去叠在一起）
    tomato: { arch: staked, fruits: { n: 8, from: 0.56, to: 0.86, ripe: 0.14 }, leafD: '#4b7c3a', leafL: '#6fa25c' },
    pepper: { arch: pepperBush, fruits: { n: 5, from: 0.62, to: 0.9, ripe: 0.12 }, leafD: '#3f7534', leafL: '#5f9a50' },
    cucumber: { arch: trellis, fruit: 'cuke', fruits: { n: 4, from: 0.66, to: 0.92, ripe: 0.10 }, bigLeaf: true, flower: 'trumpet', leafD: '#4a7c38', leafL: '#6fa25a' },
    pea: { arch: trellis, fruit: 'pea', fruits: { n: 6, from: 0.6, to: 0.9, ripe: 0.10 }, flower: '#f6f2ea', leafD: '#5d9048', leafL: '#7fb266' },
    greenbean: { arch: trellis, fruit: 'bean', fruits: { n: 6, from: 0.6, to: 0.9, ripe: 0.10 }, flower: '#f2ece0', leafD: '#4d8040', leafL: '#6fa65c' },
    zucchini: { arch: sprawl, fruit: 'long', fruits: { n: 4, from: 0.62, to: 0.9, ripe: 0.12 }, leafD: '#456f36', leafL: '#68985a' },
    pumpkin: { arch: sprawl, fruit: 'pumpkin', fruits: { n: 2, from: 0.62, to: 0.78, ripe: 0.2 }, leafD: '#4a7538', leafL: '#6d9c58' },
    corn: { arch: cornStalk, fruits: { n: 2, from: 0.68, to: 0.82, ripe: 0.16 }, leafD: '#5d8a42', leafL: '#82ae5e' },
    strawberry: { arch: strawberryPlant, fruits: { n: 6, from: 0.6, to: 0.88, ripe: 0.11 }, leafD: '#3d7534', leafL: '#5d9a50' },
    sunflower: { arch: sunflowerPlant, leafD: '#4a7c38', leafL: '#6fa25a' },
    marigold: { arch: marigoldPlant, leafD: '#4d7a3a', leafL: '#6fa055' },
    sprouts: { arch: herbPatch, herb: 'micro' },
    basil: { arch: herbPatch, herb: 'bushy' },
    chive: { arch: herbPatch, herb: 'spears' },
    mint: { arch: herbPatch, herb: 'round' },
    // ═══ 参考作物（crops.js 里 ref: true）═══
    // 全部复用现有原型，不新增绘制函数——加一种作物的画稿成本就是这一行。
    cabbage: { arch: rosette, leaf: 'round', heart: true, size: 1.28, leafD: '#5d8f4a', leafL: '#8cbb6c' },
    broccoli: { arch: rosette, leaf: 'lobed', upright: true, size: 1.15, leafD: '#3f6d38', leafL: '#63924f' },
    cauliflower: { arch: rosette, leaf: 'oval', upright: true, heart: true, size: 1.2, leafD: '#4c7a45', leafL: '#79a466' },
    napacabbage: { arch: rosette, leaf: 'spoon', upright: true, size: 1.3, leafD: '#6f9a44', leafL: '#a8c877' },
    chard: { arch: rosette, leaf: 'oval', upright: true, size: 1.15, leafD: '#2f6238', leafL: '#568a52' },
    rocket: { arch: rosette, leaf: 'lobed', size: 0.9, leafD: '#4f7f3d', leafL: '#77a85e' },
    chrysgreens: { arch: rosette, leaf: 'fern', size: 1, leafD: '#5b8c3f', leafL: '#86b263' },
    celery: { arch: rosette, leaf: 'fern', upright: true, size: 1.18, leafD: '#5a8a34', leafL: '#8ab055' },
    amaranth: { arch: rosette, leaf: 'heart', upright: true, size: 1.05, leafD: '#7a3a55', leafL: '#a4436b' },
    daikon: { arch: rootPlant, top: 'broad', root: 'taper', rootColor: '#f2f0e8', rootTop: '#cfe0a8', n: 8, leafD: '#4c8040', leafL: '#74a862' },
    parsnip: { arch: rootPlant, top: 'fern', root: 'taper', rootColor: '#e8dcb8', n: 9, size: 1.05, leafD: '#43703a', leafL: '#679455' },
    leek: { arch: spears, flat: true, n: 8, h: 40, bulb: '#eef0e2', leafD: '#4a7a52', leafL: '#6f9e72' },
    chinesechive: { arch: spears, flat: true, n: 12, h: 30, leafD: '#3f7530', leafL: '#639a4c' },
    broadbean: { arch: trellis, fruit: 'bean', fruits: { n: 6, from: 0.6, to: 0.9, ripe: 0.10 }, flower: '#efe6d8', leafD: '#4d7c4a', leafL: '#75a06a' },
    edamame: { arch: bushlow, leafD: '#4f7f36', leafL: '#7aa85a' },
    yardlong: { arch: trellis, fruit: 'bean', fruits: { n: 7, from: 0.58, to: 0.9, ripe: 0.10 }, flower: '#e8dcea', leafD: '#4a7c38', leafL: '#74a45c' },
    bittermelon: { arch: trellis, fruit: 'cuke', fruits: { n: 4, from: 0.64, to: 0.92, ripe: 0.10 }, bigLeaf: true, flower: 'trumpet', leafD: '#527f36', leafL: '#7ba85a' },
    luffa: { arch: trellis, fruit: 'cuke', fruits: { n: 4, from: 0.66, to: 0.92, ripe: 0.10 }, bigLeaf: true, flower: 'trumpet', leafD: '#42702f', leafL: '#699a4e' },
    wintermelon: { arch: sprawl, fruit: 'pumpkin', fruits: { n: 2, from: 0.62, to: 0.8, ripe: 0.2 }, leafD: '#476f3c', leafL: '#6d9560' },
    aubergine: { arch: pepperBush, fruits: { n: 4, from: 0.62, to: 0.9, ripe: 0.12 }, leafD: '#456b45', leafL: '#6a9065' },
    okra: { arch: pepperBush, fruits: { n: 5, from: 0.6, to: 0.9, ripe: 0.10 }, leafD: '#4a7a34', leafL: '#72a052' },
    coriander: { arch: herbPatch, herb: 'bushy' },
    parsley: { arch: herbPatch, herb: 'bushy' },
    dill: { arch: herbPatch, herb: 'bushy' }
  };
  // 未来新增作物的兜底：按类别套用相近档案
  const FALLBACK = {
    leaf: { arch: rosette, leaf: 'oval', upright: true },
    root: { arch: rootPlant, top: 'broad', root: 'globe', rootColor: '#c8a165' },
    fruit: { arch: staked },
    flower: { arch: marigoldPlant }
  };

  const cfgOf = crop => STYLES[crop.id] || FALLBACK[crop.category] || FALLBACK.leaf;

  // 可数器官普查：给定进度，返回此刻画面上**数得出来**的整数。
  // 拿昨天和今天各调一次做差，差出来的就是玩家今天真的能看见的新东西。
  // 发芽期（<0.10）走通用发芽序列，没有品种器官可数，一律返回空。
  function plantCensus(crop, progress) {
    const p = clamp(progress, 0, 1);
    if (p < 0.10) return {};
    const cfg = cfgOf(crop);
    const fn = cfg.arch && cfg.arch.count;
    if (!fn) return {};
    const raw = fn(p, cfg) || {};
    const out = {};
    for (const k in raw) if (raw[k] > 0) out[k] = raw[k];
    return out;
  }

  function plantArt(crop, progress, alive, windy, health = 100) {
    const p = clamp(progress, 0, 1);
    const cfg = cfgOf(crop);
    const deadMix = alive ? 0 : 0.8;
    const c = {
      crop, p, alive,
      id: crop.id,
      droop: alive ? (100 - health) / 100 * 14 : 24,
      // col：普通着色（枯萎时褪成灰褐）；col2：茎叶主色（随成熟微微加深）
      col: hex => deadMix ? mixc(hex, '#8d8474', deadMix) : hex,
      col2: col => deadMix ? mixc(col.startsWith('#') ? col : '#557a45', '#8d8474', deadMix) : col,
      leafD: mixc(cfg.leafD || '#55854a', '#38601f', p * 0.35),
      leafL: mixc(cfg.leafL || '#86b072', '#527f3d', p * 0.3)
    };
    if (!alive) { c.leafD = mixc(cfg.leafD || '#55854a', '#8d8474', 0.8); c.leafL = mixc(cfg.leafL || '#86b072', '#8d8474', 0.8); }
    let inner;
    if (p < 0.10) inner = germination(c);
    else {
      inner = cfg.arch(c, cfg);
      if (p < 0.3 && cfg.herb !== 'micro') inner = cotyPair(c, 5, 1 - t(p, 0.12, 0.3), 0.7) + inner;
    }
    // 落地投影（盆栽的影子窄一点）
    const shW = 8 + 20 * p;
    const shadow = `<ellipse cx="0" cy="2.6" rx="${px(shW)}" ry="2.4" fill="#1e150c" opacity="0.13"/>`;
    return `<g class="plant-sway ${windy ? 'windy' : ''}">${wcArt(shadow + inner)}</g>`;
  }

  plantArt.census = plantCensus;
  return plantArt;
})();
const plantCensus = plantArt.census;

if (typeof module !== 'undefined') module.exports = { plantArt, plantCensus };
